(ns ui.components.selection-pill.view
  "The selection pill of a listing. It is the ZapKit SelectionToolbar: a rounded
   bar in gray at the bottom of the listing, with the number of
   selected rows, the actions on them and the button that clears the selection.
   The classes of the bar and of the count are the generated
   `ui.components.selection-toolbar.zapkit-styles`. Since ZapKit 2.1.0 the bar does
   not style the buttons in it. The buttons are the Button of ZapKit
   (`ui.components.button.view/component`, the variant `:ghost` in the size
   `:sm`), and they have their own colours. This namespace adds what ZapKit does not have: the
   position at the bottom of the listing, and a grip. The user can drag the
   pill, or move it with the arrow keys, so that it does not hide the content
   under it. The position is kept in the browser. `dom-overrides` has these
   extra classes."
  (:require
   [frontend.local-storage :as ls]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.selection-toolbar.zapkit-styles :as toolbar-styles]
   [ui.dom-globals :as dg]
   [ui.icons :as icons]
   [ui.util :as uu]))

(def dom-overrides
  "Classes that the generated styles do not have. None of them is in ZapKit, so
   there is no upstream key to extend.

   `:wrapper` puts the pill at the bottom of the listing, 16 px from the edge. It
   does not take the pointer events of the content under it.
   `:pill` extends `[:root :base]` of the SelectionToolbar: the pill is the only
   part that takes the pointer events, and its content does not wrap. In a
   container that is narrower than the pill, `overflow-hidden` would hide the
   last actions and they could not be reached, so the pill has `overflow-x-auto`
   and scrolls sideways.
   `:grip` is the handle to drag the pill. It has the height of a ghost button,
   the cursor of a grab, and no touch scroll, so that a finger can drag it. The bar
   does not style it, so it has the text, hover, pressed and focus classes of the
   ghost Button of ZapKit."
  {:wrapper "tw2-pointer-events-none tw2-absolute tw2-inset-x-0 tw2-bottom-4 tw2-z-10 tw2-flex tw2-justify-center tw2-px-4"
   :pill    "tw2-pointer-events-auto tw2-flex-nowrap tw2-overflow-x-auto"
   :grip    "tw2-flex tw2-h-9 tw2-w-5 tw2-shrink-0 tw2-cursor-grab tw2-touch-none tw2-items-center tw2-justify-center tw2-rounded-btn focus-visible:tw2-outline-2 tw2-text-btn-ghost-text-default hover:tw2-bg-btn-ghost-bg-hover active:tw2-bg-btn-ghost-bg-pressed active:tw2-cursor-grabbing focus-visible:tw2-outline-btn-ghost-text-default focus-visible:tw2-outline-offset-2"})

(def icon-elements
  "The icons that an action can name, by key."
  {:trash  icons/trash-icon
   :pencil icons/pencil-icon
   :x      icons/x-icon})

(def zero-offset
  "The offset of the pill from its place at the bottom, in pixels."
  {:x 0
   :y 0})

(def key-step
  "The distance that an arrow key moves the pill, in pixels."
  16)

(def storage-key
  :selection-pill-offset)

(defn pill-class
  "Return the classes of the pill: the root of the generated SelectionToolbar, and
   the extra classes of `dom-overrides`."
  []
  (uu/cm (get-in toolbar-styles/selection-toolbar [:root :base])
         (:pill dom-overrides)))

(defn count-class
  "Return the classes of the text of the number of selected rows."
  []
  (get-in toolbar-styles/selection-toolbar [:count :base]))

(defn clamp-offset
  "Return the offset, moved the least that keeps the pill inside the container.
   `default-rect` is the box that the pill has with no offset. `container-rect` is the
   box of the area that the pill must stay in. A rect is a map with `:left`, `:top`,
   `:right` and `:bottom`. When the pill is larger than the container, the offset on
   that axis is 0. A missing rect gives the offset as it is."
  [{:keys [offset default-rect container-rect]}]
  (if (and default-rect container-rect)
    (let [clamp (fn [value low high]
                  (if (> low high)
                    0
                    (max low (min high value))))]
      {:x (clamp (:x offset)
                 (- (:left container-rect) (:left default-rect))
                 (- (:right container-rect) (:right default-rect)))
       :y (clamp (:y offset)
                 (- (:top container-rect) (:top default-rect))
                 (- (:bottom container-rect) (:bottom default-rect)))})
    offset))

(defn default-rect
  "Return the box that the pill has with no offset, from its box `rect` now and the
   `offset` that it has now."
  [{:keys [rect offset]}]
  (when rect
    {:left   (- (:left rect) (:x offset))
     :right  (- (:right rect) (:x offset))
     :top    (- (:top rect) (:y offset))
     :bottom (- (:bottom rect) (:y offset))}))

(defn dragged-offset
  "Return the offset of the pill while it is dragged: the offset at the start plus
   the distance that the pointer has moved, kept inside the container."
  [{:keys [start-offset start-pointer pointer default-rect container-rect]}]
  (clamp-offset
   {:offset         {:x (+ (:x start-offset) (- (:x pointer) (:x start-pointer)))
                     :y (+ (:y start-offset) (- (:y pointer) (:y start-pointer)))}
    :default-rect   default-rect
    :container-rect container-rect}))

(defn key-offset
  "Return the offset after a key of the grip, or nil when the key does not move the
   pill. An arrow key moves the pill by `key-step`. `Home` puts it back in its place."
  [{:keys [key offset default-rect container-rect]}]
  (let [moved (case key
                "ArrowLeft" (update offset :x - key-step)
                "ArrowRight" (update offset :x + key-step)
                "ArrowUp" (update offset :y - key-step)
                "ArrowDown" (update offset :y + key-step)
                "Home" zero-offset
                nil)]
    (when moved
      (clamp-offset {:offset         moved
                     :default-rect   default-rect
                     :container-rect container-rect}))))

(defn offset-style
  "Return the style that moves the pill, or nil when the pill is in its place."
  [{:keys [x y]}]
  (when-not (and (zero? (or x 0))
                 (zero? (or y 0)))
    {:transform (str "translate(" x "px, " y "px)")}))

(defn valid-offset
  "The offset from the browser storage, or the zero offset when the stored value is not
   an offset."
  [stored]
  (if (and (map? stored)
           (number? (:x stored))
           (number? (:y stored)))
    (select-keys stored [:x :y])
    zero-offset))

(defonce offset*
  (r/atom (valid-offset (ls/get storage-key))))

(defn- save-offset!
  [offset]
  (ls/set storage-key offset))

(defn action-button
  "A button of the pill. `:icon` is a key of `icon-elements`, or nil. A disabled
   button does not call `:on-click`."
  [{:keys [label icon disabled? on-click title]}]
  (let [icon-component (get icon-elements icon)]
    [button/component
     {:variant   :ghost
      :size      :sm
      :type      "button"
      :title     title
      :disabled? (boolean disabled?)
      :on-click  on-click}
     (when icon-component
       [icon-component
        {:class       "tw2-h-4 tw2-w-4 tw2-flex-shrink-0"
         :aria-hidden true}])
     label]))

(defn pill-hiccup
  "The DOM of the pill. It is a pure function of its arguments, so a test can read it.
   `:offset` is the offset of the pill. `:handlers` is a map for the wrapper, the pill and
   the grip: `:wrapper-ref`, `:pill-ref`, `:on-pointer-down`, `:on-pointer-move`,
   `:on-pointer-up`, `:on-key-down` and `:on-reset`. The grip is there when `:move-label`
   is given. The other arguments are the ones of `view`."
  [{:keys [count-label actions clear-label on-clear disabled? class move-label offset handlers]}]
  [:div
   {:class (uu/cm (:wrapper dom-overrides)
                  class)
    :ref   (:wrapper-ref handlers)}
   [:div
    {:class      (pill-class)
     :style      (offset-style (or offset zero-offset))
     :ref        (:pill-ref handlers)
     :role       "toolbar"
     :aria-label count-label}
    (when move-label
      [:button
       {:type              "button"
        :class             (:grip dom-overrides)
        :title             move-label
        :aria-label        move-label
        :on-pointer-down   (:on-pointer-down handlers)
        :on-pointer-move   (:on-pointer-move handlers)
        :on-pointer-up     (:on-pointer-up handlers)
        :on-pointer-cancel (:on-pointer-up handlers)
        :on-key-down       (:on-key-down handlers)
        :on-double-click   (:on-reset handlers)}
       [icons/grip-vertical-icon
        {:class       "tw2-h-4 tw2-w-4"
         :aria-hidden true}]])
    [:span
     {:class (count-class)}
     count-label]
    (for [{:keys [id] :as action} actions]
      ^{:key (str "selection-action-" (name id))}
      [action-button action])
    [action-button
     {:label     clear-label
      :icon      :x
      :disabled? disabled?
      :on-click  on-clear}]]])

(defn- draggable-pill
  "The pill with the behaviour of the grip. The offset is shared (`offset*`), so the pill
   is where the user put it the next time it is shown, and it is kept in the browser.
   The offset is kept inside the container when the window is resized, and also when the
   size of the pill or of the container changes, for example when the count label gets
   wider or a side panel opens."
  [_props]
  (let [wrapper-el* (atom nil)
        pill-el* (atom nil)
        drag* (atom nil)
        stop-observing* (atom nil)
        rects (fn []
                {:default-rect   (default-rect {:rect   (dg/rect-map @pill-el*)
                                                :offset @offset*})
                 :container-rect (dg/rect-map (dg/parent-node @wrapper-el*))})
        set-offset! (fn [offset]
                      (when (not= offset @offset*)
                        (reset! offset* offset)))
        reclamp! (fn [& _]
                   (when (and @pill-el* @wrapper-el*)
                     (set-offset! (clamp-offset (assoc (rects) :offset @offset*)))))
        handlers {:wrapper-ref     (fn [el]
                                     (reset! wrapper-el* el))
                  :pill-ref        (fn [el]
                                     (reset! pill-el* el))
                  :on-pointer-down (fn [e]
                                     (dg/set-pointer-capture! (dg/current-target e) (dg/pointer-id e))
                                     (reset! drag* (assoc (rects)
                                                          :start-offset @offset*
                                                          :start-pointer {:x (dg/client-x e)
                                                                          :y (dg/client-y e)})))
                  :on-pointer-move (fn [e]
                                     (when-let [drag @drag*]
                                       (set-offset! (dragged-offset (assoc drag
                                                                           :pointer {:x (dg/client-x e)
                                                                                     :y (dg/client-y e)})))))
                  :on-pointer-up   (fn [e]
                                     (when @drag*
                                       (reset! drag* nil)
                                       (dg/release-pointer-capture! (dg/current-target e) (dg/pointer-id e))
                                       (save-offset! @offset*)))
                  :on-key-down     (fn [e]
                                     (when-let [moved (key-offset (assoc (rects)
                                                                         :key (dg/event-key e)
                                                                         :offset @offset*))]
                                       (dg/prevent-default e)
                                       (set-offset! moved)
                                       (save-offset! @offset*)))
                  :on-reset        (fn [_]
                                     (set-offset! zero-offset)
                                     (save-offset! zero-offset))}]
    (r/create-class
     {:component-did-mount
      (fn [_]
        (reclamp!)
        (dg/add-event-listener! (dg/window) "resize" reclamp!)
        (reset! stop-observing* (dg/observe-resize! [@pill-el* (some-> @wrapper-el* dg/parent-node)]
                                                    reclamp!)))

      :component-will-unmount
      (fn [_]
        (dg/remove-event-listener! (dg/window) "resize" reclamp!)
        (when-let [stop! @stop-observing*]
          (stop!)))

      :reagent-render
      (fn [props]
        (pill-hiccup (assoc props
                            :offset @offset*
                            :handlers handlers)))})))

(defn view
  "Render the pill, or nothing when no row is selected.

   `:count` is the number of selected rows, and `:count-label` is its text, for
   example \"2 selected\". `:actions` is a vector of maps with `:id`, `:label`,
   `:icon`, `:disabled?`, `:title` and `:on-click`. `:clear-label` and `:on-clear`
   are the text and the handler of the button that clears the selection.
   `:move-label` is the name of the grip that moves the pill. The pill has no grip
   when it is not given. `:class` is added to the wrapper."
  [{:keys [count] :as props}]
  (when (pos? (or count 0))
    [draggable-pill props]))
